/* eslint-disable no-prototype-builtins */
// @ts-strict-ignore
import { Subject, Subscription } from 'rxjs';
import { SHA256 } from 'crypto-es';
import { AuthSettings } from 'rainbow-web-sdk/models/authSettings.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { Service } from 'rainbow-web-sdk/services/service';
import fetch from 'rainbow-web-sdk/utils/fetchWithTimeout';
import { wait } from 'rainbow-web-sdk';
import { MultiFactorAuthFeatureInfo, MultiFactorAuthInformation, MultiFactorAuthPolicy, MultiFactorAuthVerifInfo } from 'rainbow-web-sdk/models/rainbow-auth.model';
import { ErrorHelperService } from '../utilities';
import randomString from 'rainbow-web-sdk/utils/randomString';
import { InvalidTokenError, jwtDecode } from 'jwt-decode';
import { intervalToDuration, formatDuration } from 'date-fns';
import { MainService } from './main.service';

declare const imprint: any;

(window as any).moduleIds = [];
(window as any).moduleIds.push("$$APP_PWD_PART1$$");
(window as any).moduleIds.push("$$APP_PWD_PART2$$");
(window as any).moduleIds.push("$$APP_PWD_PART3$$");
(window as any).moduleIds.push("$$APP_PWD_PART4$$");

export const AUTH_SVC = 'AuthService';

export enum AUTO_AUTH_TYPES {
    CommandLine = 'CommandLine',
    AuthToken = 'AuthToken',
    KeyChain = 'KeyChain',
    NoAuth = 'NoAuth',
}

export enum AUTH_STATUS {
    Failure = "Failure",
    Success = "Success"
}

export enum AUTH_TYPE {
    RAINBOW = 'RAINBOW',
    SAML = 'SAML',
    OIDC = 'OIDC',
    TOTP = 'TOTP',
    PASSWORDLESS = 'PASSWORDLESS'
}
export interface AutoAuthResult {
    type: AUTO_AUTH_TYPES;
    status?: AUTH_STATUS;
    error?: Error;
    errorCode?: string;
    authInfo?: any;
}

/**
 * @eventProperty
 */
export enum AuthServiceEvents {

    /**
     * @eventProperty
     * This RB event is send when a conversation has been removed from the user (or from another client)
     */
    RAINBOW_ON_AUTH_TOKEN_EXPIRE = 'RAINBOW_ON_AUTH_TOKEN_EXPIRE',

    /**
     * @eventProperty
     * This RB event is send when a conversation has been removed from the user (or from another client)
     */
    RAINBOW_ON_AUTH_TOKEN_WILL_EXPIRE = 'RAINBOW_ON_AUTH_TOKEN_WILL_EXPIRE',

    /**
     * @eventProperty
     * This RB event is send when a conversation has been removed from the user (or from another client)
     */
    RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW = 'RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW',

    /**
     * @eventProperty
     * This RB event is send when a conversation has been removed from the user (or from another client)
     */
    RAINBOW_ON_AUTH_TOKEN_RENEW = 'RAINBOW_ON_AUTH_TOKEN_RENEW',

    /**
     * @eventProperty
     * This RB event is sent when the redirection from external browser to rainbow is done for the backchannel authent (used for Azure Cond Access)
     */
    RAINBOW_ON_AUTH_BACKCHANNEL_REDIRECT_COMMAND = 'RAINBOW_ON_AUTH_BACKCHANNEL_REDIRECT_COMMAND',
    /**
     * @eventProperty
     * This RB event is sent when a successful token is retrieved by backchannel authent (used for Azure Cond Access)
     */
    RAINBOW_ON_AUTH_BACKCHANNEL_SUCCESS = 'RAINBOW_ON_AUTH_BACKCHANNEL_SUCCESS',
    /**
     * @eventProperty
     * This RB event is sent when a failure occurs in token retrieval in backchannel authent (used for Azure Cond Access)
     */
    RAINBOW_ON_AUTH_BACKCHANNEL_FAILURE = 'RAINBOW_ON_AUTH_BACKCHANNEL_FAILURE'

}
export class AuthService extends Service {

    public SSO_LOGIN_POPUP_WINDOW_NAME = "Single Sign-On Login";

    public token: string = null;
    public login: string = null;
    public userId: string = null;
    public jidIm: string = null;
    public jidTel: string = null;
    public jidPwd: string = null;
    public xmppDomain: string = null;
    public companyId: string = null;
    public firstUseNewVersion: boolean = false;
    public initialized: boolean = false;
    public isGuest: boolean = false;
    public loggedInUser: any;
    public authType: AUTH_TYPE = AUTH_TYPE.RAINBOW;
    public sessionShouldBeUpdated: boolean = false;
    public sessionExpirationTimestamp: number;

    private authSettings: AuthSettings = null;
    public browserFingerprint: string = null;
    private renewTokenTimer?: number;
    private renewSessionTimer?: number;
    private tokenSurveyTimer?: number;
    private win = window as any;
    private globalSSOSettings: AuthSettings = null;
    private rxSubject: Subject<any> = new Subject<any>();
    private logger: LoggerService = LoggerService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private mainService: MainService = MainService.getInstance();

    private backChannelPollingTimeout: any;
    private backChannelRenewSession: boolean;
    private rememberMe: boolean;

    private randomFullJid: string;

    public static getInstance() {
        return Service.create(AUTH_SVC, AuthService);
    }

    public static build() {
        return new AuthService();
    }

    private constructor() {
        super();
        const storedVersion = localStorage.getItem('version');
        const storedMajorVersion = this.getMajorVersion(storedVersion);
        const currentMajorVersion = this.getMajorVersion(this.win.version);
        if (!config.appModuleKey) { config.appModuleKey = this.win.moduleIds ? this.win.moduleIds.join('') : 'default'; }
        if (storedMajorVersion !== currentMajorVersion) { this.firstUseNewVersion = true; }
        localStorage.setItem('version', this.win.version);
        this.getServerApiUrls();

        if (localStorage?.webinarOptionAllowMultitab === 'true' || config.webinarOptionAllowMultitab === true) {
            this.logger.info(`[AuthService] webinar debug option : ignore token storage update`);
        }
        else {
            window.addEventListener('storage', (event: StorageEvent) => {
                if (event.key === 'token' && event.newValue) {
                    this.logger.info('[AuthService] Detect token change, restart token survey mechanism');
                    this.token = event.newValue;
                    this.startTokenSurvey();
                }
            }, false);
        }
    }

    public subscribe(handler: any): Subscription { return this.rxSubject.subscribe(handler); }
    public sendEvent(eventName: AuthServiceEvents, data?: any): void { this.rxSubject.next(RBEvent.create(eventName, data)); }

    public async logon(login: string, password: string, rememberMe: boolean = true, authSettings?: AuthSettings): Promise<{ mfaPolicy: MultiFactorAuthPolicy; authType: AUTH_TYPE.TOTP; token: string; } | null> {
        try {
            // Check login email syntax
            // if (login.indexOf("@") === -1) { throw this.createError(`Wrong login format '${login}'`, 'errorUnauthorized', 401500, undefined, true); }

            // Rainbow login url was given by the server (otherwise use the default url)
            let url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/login`;
            if (authSettings?.type === AUTH_TYPE.RAINBOW) { url = authSettings.loginUrl; }
            else if (authSettings?.safetyUrl) { url = authSettings.safetyUrl; this.authType = authSettings.type; }

            // Compute authentication request headers
            const headers: any = {};
            headers.Authorization = `Basic ${window.btoa(unescape(encodeURIComponent(login + ":" + password)))}`;
            headers.Accept = 'application/json';
            this.setInformationHeaders(headers);

            // Set id associated to the application which realizes the login operation
            // This id will be checked to the ones stored in datababase in case of multifactor authentication is activated for the user and configured to be run only for only unstrusted application
            const trustedAppId = localStorage.getItem("x-rainbow-trusted-appid");
            if (trustedAppId) { headers["x-rainbow-trusted-appid"] = trustedAppId; }

            if (config.rainbowApiClientKey) {
                const clientKey = config.rainbowApiClientKey + ":" + SHA256(config.appModuleKey + password);
                headers["x-rainbow-app-auth"] = "Basic " + window.btoa(unescape(encodeURIComponent(clientKey)));
            }
            headers["x-rainbow-resource"] = this.getRandomFullJid();

            // Call authentication REST api
            const response: Response = await fetch(url, { method: 'GET', headers, credentials: 'include' });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();

            // Extract authToken from response
            this.token = responseData.token;

            // Handle mfa login
            if (responseData.mfaRequired) {
                this.logger.info(`[AuthService] mfaRequired set to true, use token authentication`);
                return { mfaPolicy: responseData.mfaPolicy, authType: AUTH_TYPE.TOTP, token: this.token };
            }
            this.logger.info(`[AuthService] no mfaRequired use basic authentication`);

            this.extractUserInfo(responseData.loggedInUser);

            // Update credentials local storage storing policy
            if (rememberMe) {
                this.logger.info("[AuthService] Store authentication info in local storage");
                localStorage.setItem('login', this.login);
                localStorage.setItem('id', this.userId);
                localStorage.setItem('token', this.token);
                localStorage.setItem('authType', this.authType);
            }
            else { this.removeCredentials(); }

            localStorage.setItem('firstUse', 'false');
            const loginForLogs = login.charAt(0) + login.replace(/[^\s](?=.{1,}$)/g, "*").substr(1);
            this.logger.info(`[AuthService] logon () with ${loginForLogs} -- SUCCESS`);
            this.startTokenSurvey();
            return null;
        }
        catch (error) {
            const loginForLogs = login.charAt(0) + login.replace(/[^\s](?=.{1,}$)/g, "*").substr(1);
            let errorMessage: string = `logon () with ${loginForLogs} -- FAILURE -- `;
            if (error.localError) {
                this.logger.error(`[AuthService] ${errorMessage} ${error}`);
                throw error;
            }
            let errorStatus: string = 'errorUltimateUnknownError';
            let errorCode = null;
            let errorData = null;

            if (error.status === 0 || error.status === -1) {
                errorMessage += "No server response";
                errorStatus = "errorNoServerResponse";
            }
            else if (error.json) {
                const responseData: any = await error.json();
                if (error.status === 401) {
                    this.removeCredentials();
                    errorMessage += responseData.errorDetails;
                    errorCode = responseData.errorDetailsCode;

                    switch (errorCode) {
                        case 401500: errorStatus = 'errorUnauthorized'; break;
                        case 401700: errorStatus = 'errorWrongApplicationId'; break;
                        case 401522: errorStatus = "errorUnauthorized"; break;
                        case 401223: errorStatus = "errorLogNotAuthorized"; break;
                        case 401501:
                            errorStatus = "errorAccountLocked";
                            if (responseData.errorDetailsData) { errorData = { delay: responseData.errorDetailsData.forbiddenDelay }; }
                            break;
                        default: break;
                    }
                }
                else if (error.status === 500) {
                    errorMessage += responseData.errorDetails;
                    errorStatus = "errorInternalServerError";
                }
            }

            this.logger.error(`[AuthService] ${errorMessage}`);
            throw this.createError(errorMessage, errorStatus, errorCode, errorData);
        }
    }

    // Warning this method is overided in centralized service
    public getRandomFullJid(jid: string = null): string {
        if (!this.randomFullJid) {
            const OS = (navigator.platform.toUpperCase().indexOf("MAC") >= 0) ? "osx" : "win";
            let platform = `desk_${OS}`;
            if (!this.mainService.isDesktopApp()) { platform = config.sdk ? 'web_sdk' : `web_${OS}`; }

            this.randomFullJid = `${platform}_${version}_${randomString(8)}`;
        }

        return (jid) ? `${jid}/${this.randomFullJid}` : this.randomFullJid;
    }

    public async logonOneTimeGuest(login: string, password: string): Promise<{ mfaPolicy: MultiFactorAuthPolicy; authType: AUTH_TYPE.TOTP; token: string; } | null> {
        try {
            // Check login email syntax
            // if (login.indexOf("@") === -1) { throw this.createError(`Wrong login format '${login}'`, 'errorUnauthorized', 401500, undefined, true); }

            // Rainbow login url was given by the server (otherwise use the default url)
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/login`;

            // Compute authentication request headers
            const headers: any = {};
            headers.Authorization = `Basic ${window.btoa(unescape(encodeURIComponent(login + ":" + password)))}`;
            headers.Accept = 'application/json';
            this.setInformationHeaders(headers);
            if (config.rainbowApiClientKey) {
                const clientKey = config.rainbowApiClientKey + ":" + SHA256(config.appModuleKey + password);
                headers["x-rainbow-app-auth"] = "Basic " + window.btoa(unescape(encodeURIComponent(clientKey)));
            }

            // Call authentication REST api
            const response: Response = await fetch(url, { method: 'GET', headers, credentials: 'include' });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();

            // Extract authToken from response
            this.token = responseData.token;

            this.extractUserInfo(responseData.loggedInUser);

            this.logger.info(`[AuthService] logon () with ${login} -- SUCCESS`);
            this.startTokenSurvey();
            return null;
        }
        catch (error) {
            let errorMessage: string = `logon () with ${login} -- FAILURE -- `;
            if (error.localError) {
                this.logger.error(`[AuthService] ${errorMessage} ${error}`);
                throw error;
            }
            let errorStatus: string = 'errorUltimateUnknownError';
            let errorCode = null;
            let errorData = null;

            if (error.status === 0 || error.status === -1) {
                errorMessage += "No server response";
                errorStatus = "errorNoServerResponse";
            }
            else if (error.json) {
                const responseData: any = await error.json();
                if (error.status === 401) {
                    errorMessage += responseData.errorDetails;
                    errorCode = responseData.errorDetailsCode;

                    switch (errorCode) {
                        case 401500: errorStatus = 'errorUnauthorized'; break;
                        case 401700: errorStatus = 'errorWrongApplicationId'; break;
                        case 401522: errorStatus = "errorUnauthorized"; break;
                        case 401223: errorStatus = "errorLogNotAuthorized"; break;
                        case 401501:
                            errorStatus = "errorAccountLocked";
                            if (responseData.errorDetailsData) { errorData = { delay: responseData.errorDetailsData.forbiddenDelay }; }
                            break;
                        default: break;
                    }
                }
                else if (error.status === 500) {
                    errorMessage += responseData.errorDetails;
                    errorStatus = "errorInternalServerError";
                }
            }

            this.logger.error(`[AuthService] ${errorMessage}`);
            throw this.createError(errorMessage, errorStatus, errorCode, errorData);
        }
    }

    public async logonWithToken(token?: string) {
        try {
            // If no available token in URL, get token from localStorage
            if (!token) { token = localStorage.getItem('token'); }
            if (!token) throw new Error('No token');

            this.logger.info("[AuthService] logonWithToken");

            // Extract token info
            const decodedToken: any = jwtDecode(token);
            this.token = token;
            this.login = decodedToken.user.loginEmail;
            this.userId = decodedToken.user.id;

            this.authType = AUTH_TYPE.RAINBOW;
            if (decodedToken.saml) { this.authType = AUTH_TYPE.SAML; }
            if (decodedToken.oidc) { this.authType = AUTH_TYPE.OIDC; }

            // Store token info in local storage
            localStorage.setItem('token', this.token);
            localStorage.setItem('login', this.login);
            localStorage.setItem('id', this.userId);
            localStorage.setItem('authType', this.authType);

            // Get user data (the login in fact)
            await new Promise(res => setTimeout(res, 2000));
            await this.getUserData();
            await this.startTokenSurvey();
            this.authSettings = await this.getUserAuthenticationSettings(this.login);
        }
        catch (error) {
            const errorMessage = error ? error.message : "unknownError";
            if (errorMessage === 'No token') { this.logger.debug('[AuthService] AuthenticateWithToken -- No available token'); }
            else { this.logger.error(`[AuthService] AuthenticateWithToken -- FAILURE -- ${errorMessage}`); }
            throw new Error(errorMessage);
        }
    }

    public async sdkLogOn(login: string, password: string, host: string, appToken: any, token: string) {
        this.getServerApiUrls();
        if (token) return this.logonWithToken(token);
        return this.logon(login, password, false);
    }

    public async setRenewedToken(token: string = null): Promise<void> {
        try {
            this.logger.debug(`[AuthService] setRenewedToken with ${token}`);
            this.token = token;
            await wait(2000);
            localStorage.setItem('token', this.token);
            this.startTokenSurvey();
            this.logger.debug("[AuthService] sdkSetRenewedToken -- reStartTokenSurvey -- SUCCESS");
        }
        catch (error) {
            const newError = (!error?.message) ? new Error("unknownError") : error;
            this.logger.error("[AuthService] setRenewedToken -- FAILURE -- " + newError.message);
            throw newError;
        }
    }

    public async logout(): Promise<string> {

        //call logout API on Rainbow
        try {
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/logout`;
            // Compute authentication request headers
            const headers = this.getRequestHeader();
            headers["x-rainbow-resource"] = this.getRandomFullJid();
            const response = await fetch(url, { method: "GET", headers });
            if (!response.ok) {
                this.logger.error("[AuthService] Logout ERROR -- continue anyway");
            }
        }
        catch (error) {
            this.logger.error(`[AuthService] Logout ERROR ${error} -- continue anyway`);
        }

        // Was the token gotten from SSO authentication ?
        if (this.authSettings?.logoutUrl && this.authType !== AUTH_TYPE.RAINBOW) {
            this.logger.info("[AuthService] Logout -- " + this.authType);

            // The token must be passed as extra parameter of the logout url
            this.authSettings.setParameter("token", () => { return this.token; });

            this.removeCredentials();

            // Redirec to SAML logout url
            return this.authSettings.logoutUrl;
        }

        this.removeCredentials();
        this.logger.info("[AuthService] Logout -- SUCCESS");
        return null;
    }

    public async getGlobalSSOConfiguration(): Promise<any> {
        try {
            const defaultLoginUrl = `${config.restServerUrl}/api/rainbow/authentication/v1.0/login`;
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/global-sso`;
            const headers = {}; this.setInformationHeaders(headers);

            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }
            this.logger.info("[AuthService] getGlobalSSOConfiguration -- SUCCESS");
            let responseData: any = await response.json();
            let asipSanteConfig = null;
            responseData = responseData.data;
            if (responseData?.length) {
                responseData.forEach((ssoConfig: any) => {
                    this.logger.info("[AuthService] getGlobalSSOConfiguration -- type: " + ssoConfig.type);
                    if (ssoConfig.type === "ASIP_SANTE") { asipSanteConfig = ssoConfig; }
                });
            }

            if (asipSanteConfig) {
                const authSettings = AuthSettings.createFromData([asipSanteConfig]);

                // The user unique identifier: typicaly his login email

                // The HTTP header custom field "x-rainbow-app-auth" is used to authenticate an application
                authSettings.setParameter('x-rainbow-app-auth', (challenge: string) => { return this.getRainbowAppAuth(config.rainbowApiClientKey, config.appModuleKey, challenge); });

                // Development mode
                if (window && (window.location.href.indexOf("localhost") !== -1 || window.location.href.indexOf("openrainbow.red") !== -1)) {
                    // force a safety login url, if not already set, when SSO login is enabled for the identified user
                    authSettings.setParameter('safetyUrl', () => { return defaultLoginUrl; });
                }
                this.globalSSOSettings = authSettings;
            }

            return this.globalSSOSettings;
        }
        catch (error) {
            let errorMessage = 'getGlobalSSOConfiguration failure : ';
            let errorStatus = "errorUltimateUnknownError";

            if ((error.message && error.message === 'timeout') || error.status === 0 || error.status === -1) {
                errorMessage += "No server response";
                errorStatus = "errorNoServerResponse";
            }
            else if (error.json) {
                const responseData: any = await error.json();
                if (error.status === 400 || error.status === 404) {
                    errorMessage += responseData.errorDetails;
                    errorStatus = "errorUnauthorized";
                }
                else if (error.status === 500) {
                    errorMessage += responseData.errorDetails;
                    errorStatus = "errorInternalServerError";
                }
            }
            else {
                errorMessage += 'Unknown error';
                errorStatus = "errorUltimateUnknownError";
            }

            // Log pb information
            this.logger.info("[AuthService] " + errorMessage);

            // Reject the promise
            const newError: any = new Error(errorMessage);
            newError.details = errorStatus;
            throw newError;
        }
    }

    public async getUserAuthenticationSettings(login: string = null, inAppAuth: boolean = false, country?: string, isDesktopApp?: boolean) {
        try {
            const defaultLoginUrl = `${config.restServerUrl}/api/rainbow/authentication/v1.0/login`;
            const localStorageLang: string = localStorage.getItem('lang');

            if (config.disableSSO) { inAppAuth = true; }
            if (inAppAuth) {
                // Default authentication URL (Rainbow authentication)
                return AuthSettings.createFromData([{ type: AUTH_TYPE.RAINBOW, loginUrl: defaultLoginUrl }]);
            }

            // Else get SSO auth information
            let url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/urls?uid=${encodeURIComponent(login)}`;
            if (localStorageLang) {
                url += `&uiLocales=${localStorageLang}`;
            }
            if (country) {
                url += `&country=${country}`;
            }
            if (isDesktopApp) {
                url += `&useBackchannelPolling=true`;
            }
            const headers = {}; this.setInformationHeaders(headers);

            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();
            const authSettings = AuthSettings.createFromData(responseData.data);

            // The user unique identifier: typicaly his login email
            authSettings.setParameter('uid', () => { return login; });

            // The HTTP header custom field "x-rainbow-app-auth" is used to authenticate an application
            authSettings.setParameter('x-rainbow-app-auth', (challenge: string) => { return this.getRainbowAppAuth(config.rainbowApiClientKey, config.appModuleKey, challenge); });

            // Development mode
            if (window && (window.location.href.indexOf("localhost") !== -1 || window.location.href.indexOf("openrainbow.red") !== -1)) {
                // force a safety login url, if not already set, when SSO login is enabled for the identified user
                authSettings.setParameter('safetyUrl', () => { return defaultLoginUrl; });
            }
            return authSettings;
        }
        catch (error) {
            let errorMessage = 'authenticate for login "' + login + '" failure : ';
            let errorStatus = "errorUltimateUnknownError";
            let errorDetailsCode: number;

            if ((error.message && error.message === 'timeout') || error.status === 0 || error.status === -1) {
                errorMessage += "No server response";
                errorStatus = "errorNoServerResponse";
            }
            else if (error.json) {
                const responseData: any = await error.json();
                errorDetailsCode = responseData.errorDetailsCode;
                if (responseData.errorDetailsCode === 400210) {
                    errorMessage = "invalidPhoneNumber";
                    errorStatus = "invalidPhoneNumber";
                } else if (responseData.errorDetailsCode === 400915) {
                    errorMessage = "noUserFound";
                    errorStatus = "errorUnauthorized";
                } else if (error.status === 400 || error.status === 404) {
                    errorMessage += responseData.errorDetails;
                    errorStatus = "errorUnauthorized";
                }
                else if (error.status === 500) {
                    errorMessage += responseData.errorDetails;
                    errorStatus = "errorInternalServerError";
                }
            }
            else {
                errorMessage += "unknownError";
                errorStatus = "errorUltimateUnknownError";
            }

            // Log pb information
            this.logger.info("[AuthService] " + errorMessage);

            // Reject the promise
            const newError: any = new Error(errorMessage);
            newError.details = errorStatus;
            if (errorDetailsCode) newError.errorDetailsCode = errorDetailsCode;
            throw newError;
        }
    }

    public removeCredentials(): void {
        this.authType = AUTH_TYPE.RAINBOW;
        //this.userId = undefined;  MCO: Really useful ? i commented it (CCH)
        localStorage.removeItem('id');
        localStorage.removeItem('token');
        localStorage.removeItem('authType');
        localStorage.removeItem('token');
        localStorage.removeItem('resourceXmppId');
        this.randomFullJid = '';
        if (this.isGuest) { localStorage.removeItem('login'); this.isGuest = false; }
        this.resetUserEnvironment();
        this.logger.debug("[AuthService] removeCredentials -- SUCCESS");
    }

    public resetUserEnvironment(): void {
        if (config.defaultEnvironment) {
            config.xmpp.currentServer = config.defaultEnvironment;
            config.xmpp.server = config.defaultEnvironment;
            config.webservices.currentServer = config.defaultEnvironment;
            config.webservices.server = config.defaultEnvironment;
        }
    }

    public getRequestHeader(accept?: string): any {
        return { Authorization: `Bearer ${this.token}`, Accept: (accept ? accept : 'application/json') };
    }

    public getRequestHeaderWithRange(range: any): any {
        const header: any = this.getRequestHeader();
        header.Range = range;
        return header;
    }

    public getPostHeader(contentType: string = null, accept: string = null): any {
        const header = this.getRequestHeader(accept);
        header['Content-Type'] = contentType || "application/json";
        return header;
    }

    public getPostHeaderWithRange(range: any, contentType: string): any {
        const header = this.getPostHeader(contentType);
        header['Content-Range'] = range;
        return header;
    }

    // Methods overloaded by centralizedService for Rainbow
    public setInformationHeaders(headers: any): void {
        headers["x-rainbow-client"] = this.getInformationHeadersApp();
        headers["x-rainbow-client-version"] = this.getInformationHeadersAppVersion();
    }

    public getInformationHeadersApp(): string { return "web_sdk"; }
    public getInformationHeadersAppVersion(): string {
        const winAsAny: any = window;
        return winAsAny.sdkversion ? winAsAny.sdkversion : '9.9.99';
    }

    public async startTokenSurvey(): Promise<void> {
        try {
            if (!this.token || (typeof this.token !== "string") || !this.token.length) { this.logger.info("[AuthService] No token."); return; }
            const decodedToken: any = jwtDecode(this.token);
            const countRenewed = decodedToken.countRenewed;
            const maxTokenRenew = decodedToken.maxTokenRenew;
            const tokenIssueAtTimestamp = decodedToken.iat * 1000;
            const tokenExpirationTimestamp = decodedToken.exp * 1000;

            // Compute Duration of the token
            const tokenDuration = tokenExpirationTimestamp - tokenIssueAtTimestamp;

            // Compute session start and expiration dates
            const sessionStartDate = new Date(tokenIssueAtTimestamp - countRenewed * tokenDuration / 2);
            this.sessionExpirationTimestamp = tokenIssueAtTimestamp + (tokenDuration / 2) * (maxTokenRenew + 1 - countRenewed);

            this.logger.info(`[AuthService] startTokenSurvey -- Session start date ${sessionStartDate} expiration date ${new Date(this.sessionExpirationTimestamp)}`);

            // Compute duration until expiration
            const durationUntilExpiration = tokenExpirationTimestamp - (new Date()).valueOf();
            this.logger.info(`[AuthService] startTokenSurvey -- Session current token expires in ${this.formatDuration(durationUntilExpiration)} (duration: ${this.formatDuration(tokenDuration)}, countRenewed: ${countRenewed}, maxRenew: ${maxTokenRenew})`);

            // Compute duration until next RAINBOW_ON_AUTH_TOKEN_WILL_EXPIRE event
            const halfTokenDuration = tokenDuration / 2;
            const durationUntilNextWillExpireEvent = durationUntilExpiration - halfTokenDuration;
            this.logger.info(`[AuthService] startTokenSurvey -- Session current token ON_AUTH_TOKEN_WILL_EXPIRE event will be fired in ${this.formatDuration(durationUntilNextWillExpireEvent)}`);

            // Compute duration until next RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW event
            const nextShouldRenewEventTimestamp = tokenIssueAtTimestamp + (tokenDuration / 2) * (2 / 3 * (maxTokenRenew + 1) - countRenewed);
            const nextShouldRenewEventDate = new Date(nextShouldRenewEventTimestamp);
            const todayDate = new Date();
            const todayTimestamp = todayDate.valueOf();
            const durationUntilNextSessionShouldRenewEvent = nextShouldRenewEventTimestamp - todayTimestamp;
            if (durationUntilNextSessionShouldRenewEvent <= 0) this.logger.info(`[AuthService] startTokenSurvey -- Session RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW event will be fired NOW !`);
            else this.logger.info(`[AuthService] startTokenSurvey -- Session RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW event will be fired in ${this.formatDuration(durationUntilNextSessionShouldRenewEvent)} (${nextShouldRenewEventDate})`);

            // Always clear RAINBOW_ON_AUTH_TOKEN_WILL_EXPIRE and RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW event timers
            if (this.renewTokenTimer) { clearTimeout(this.renewTokenTimer); this.renewTokenTimer = undefined; }
            if (this.renewSessionTimer) { clearTimeout(this.renewSessionTimer); this.renewSessionTimer = undefined; }
            if (this.tokenSurveyTimer) { clearTimeout(this.tokenSurveyTimer); this.tokenSurveyTimer = undefined; }

            // If token as already expire send ON_AUTH_TOKEN_EXPIRE event
            if (durationUntilExpiration < 0) {
                this.logger.info("[AuthService] startTokenSurvey -- Token has already expired send RAINBOW_ON_AUTH_TOKEN_EXPIRE event");
                this.token = null;
                this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_TOKEN_EXPIRE);
                const error: any = new Error("Token expired");
                error.details = 'AUTH_TOKEN_EXPIRED';
                throw error;
            }

            // If token will expire in less than half token duration then send ON_AUTH_TOKEN_WILL_EXPIRE
            if (durationUntilExpiration < halfTokenDuration) {
                this.logger.info("[AuthService] startTokenSurvey send ON_AUTH_TOKEN_WILL_EXPIRE event");
                this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_TOKEN_WILL_EXPIRE);
                if (decodedToken.oauth === undefined) {
                    this.logger.info("[AuthService] startTokenSurvey -- try to re-new it immediately");
                    await this.renewAuthToken();
                }
                return;
            }

            // Manage RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW event
            if (durationUntilNextSessionShouldRenewEvent <= 0) this.sendAuthSessionShouldRenewEvent();
            else {
                if (durationUntilNextSessionShouldRenewEvent < 86400000) /*24H*/ {
                    this.logger.info("[AuthService] startTokenSurvey -- session should renew event should be fire in less than 24 hours - start timer");
                    this.renewSessionTimer = window.setTimeout(() => { this.sendAuthSessionShouldRenewEvent(); }, durationUntilNextSessionShouldRenewEvent);
                }
                this.sendAuthSessionShouldRenewEvent(false);
            }

            // Set timer to manage next RAINBOW_ON_AUTH_TOKEN_WILL_EXPIRE event
            this.renewTokenTimer = window.setTimeout(() => {
                if (decodedToken.oauth === undefined) { this.renewAuthToken(); }
                else { this.startTokenSurvey(); }
            }, durationUntilNextWillExpireEvent);

            // Set 12 hours timer to manage RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW timer (setTimeout limit)
            this.tokenSurveyTimer = window.setTimeout(() => {
                this.logger.info('[AuthService] Token survey periodic verification');
                this.startTokenSurvey();
            }, 43200000); /*12H*/
            return;

        }
        catch (error) {
            const newError = (!error?.message) ? new Error("unknownError") : error;
            this.logger.error("[AuthService] startTokenSurvey -- FAILURE -- " + newError.message);
            throw newError;
        }
    }

    private sendAuthSessionShouldRenewEvent(value?: boolean): void {
        if (value !== undefined) {
            this.sessionShouldBeUpdated = value;
            this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW, value);
            this.logger.info(`[AuthService] sendEvent ${AuthServiceEvents.RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW} : value`);
            return;
        }
        const sessionUpdateLaterDate = new Date(localStorage.sessionUpdateLaterDate);
        const shouldRenewEventSuspended = (sessionUpdateLaterDate && sessionUpdateLaterDate.getDate() === (new Date()).getDate());
        if (this.sessionShouldBeUpdated !== !shouldRenewEventSuspended) {
            this.sessionShouldBeUpdated = !shouldRenewEventSuspended;
            this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW, this.sessionShouldBeUpdated);
            this.logger.info(`[AuthService] sendEvent ${AuthServiceEvents.RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW} : ${this.sessionShouldBeUpdated}`);
        }
    }

    public suspendRenewSessionEventing(): void {
        localStorage.sessionUpdateLaterDate = new Date();
        this.sessionShouldBeUpdated = false;
        this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW, false);
        this.logger.info(`[AuthService] sendEvent ${AuthServiceEvents.RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW} : ${this.sessionShouldBeUpdated}`);
    }

    public stopTokenSurvey() {
        if (this.renewTokenTimer) { clearTimeout(this.renewTokenTimer); }
        if (this.renewSessionTimer) { clearTimeout(this.renewSessionTimer); }
        if (this.tokenSurveyTimer) { clearTimeout(this.tokenSurveyTimer); }
        this.logger.debug("[AuthService] StopTokenSurvey -- SUCCESS");
    }

    public getRainbowAppAuth(appId: string, appSecret: string, password: string = null): string {
        return window.btoa(unescape(encodeURIComponent((appId || "") + ":" + SHA256((appSecret || "") + (password || "")))));
    }

    private getMajorVersion(appVersion: string): string {
        if (!appVersion) { return null; }
        const regExp = /\d+\.\d+/g;
        const result = regExp.exec(appVersion);
        if (result && result.length > 0) { return result[0]; }
        return null;
    }

    public getServerApiUrls(): void {

        // Handle server data
        const server = localStorage.getItem('server');
        if (server && server !== 'null' && server !== 'undefined') {
            this.logger.debug(`[AuthService] getServerApiUrls -- Find server data from local storage -- ${server}`);
            config.xmpp.currentServer = config.webservices.currentServer = server;
            if (server === 'openrainbow.net' || server === 'openrainbow.xyz') {
                config.appModuleKey = config.dotNetAppModuleKey;
                config.rainbowApiClientKey = config.dotNetRainbowApiClientKey;
                config.multiEnvServer = 'openrainbow.net';
            }
            if (server === 'openrainbow.com' || server === 'openrainbow.red') {
                config.appModuleKey = config.dotComAppModuleKey;
                config.rainbowApiClientKey = config.dotComRainbowApiClientKey;
                config.multiEnvServer = 'openrainbow.com';
            }
            if (server === 'qa.openrainbow.net') {
                config.appModuleKey = config.dotQaNetAppModuleKey;
                config.rainbowApiClientKey = config.dotQaNetRainbowApiClientKey;
                config.multiEnvServer = 'qa.openrainbow.net';
            }
            if (!config.defaultEnvironment) { config.defaultEnvironment = config.xmpp.currentServer; }
        }
        else {
            if (config.xmpp) {
                config.xmpp.currentServer = config.xmpp.server;
                config.webservices.currentServer = config.webservices.server;
                this.logger.debug(`[AuthService] getServerApiUrls -- Use default config server -- ${config.xmpp.server}`);
            }
        }

        // Compute REST server URL
        if (config.webservices) {
            config.restServerUrl = `${config.webservices.protocol}://${config.webservices.currentServer}:${config.webservices.port}`;
        }
    }

    public async renewAuthToken(restartTokenSurvey: boolean = false) {
        try {
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/renew`;
            const response = await fetch(url, { method: 'GET', headers: this.getRequestHeader() });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();
            this.logger.info("[AuthService] renewAuthToken -- Renew authentication token success");
            this.token = responseData.token;
            localStorage.setItem('token', this.token);
            this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_TOKEN_RENEW);
            if (!restartTokenSurvey || restartTokenSurvey) { this.startTokenSurvey(); }
        }
        catch (error) {
            if (this.renewTokenTimer) { clearTimeout(this.renewTokenTimer); }

            let errorDetails = '';
            if (error.json) {
                const responseData: any = await error.json();
                errorDetails = responseData?.errorDetails ? responseData.errorDetails : "no details";
                const errorMessage = "Renew authentication token failure -- " + errorDetails;
                const newError: any = new Error(errorMessage);
                newError.details = "AUTH_TOKEN_EXPIRED";
                this.logger.error("[AuthService] renewAuthToken -- FAILURE -- " + errorMessage);
                this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_TOKEN_EXPIRE);
                throw newError;
            }
            else {
                const errorMessage = "Renew authentication token failure -- " + error.message;
                this.logger.error("[AuthService] renewAuthToken -- FAILURE -- " + errorMessage);
            }
        }
    }

    public async getUserData(): Promise<any> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.userId}`;
            const response = await fetch(url, { method: 'GET', headers: this.getRequestHeader() });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();
            this.extractUserInfo(responseData.data);
        }
        catch (error) {
            this.removeCredentials();
            if (error instanceof Response) throw new Error("Wrong token -- " + error.statusText);
            throw new Error("Wrong token -- " + error.message);
        }
    }

    public async getUserEnvironments(login: string): Promise<any> {
        try {
            if (!config.multiEnvServer) { return [{ environmentApiUrl: "https://" + config.xmpp.currentServer }]; }
            if (!config.defaultEnvironment) { config.defaultEnvironment = config.xmpp.server; }
            const url = config.webservices.protocol + "://" + config.multiEnvServer + ":" + config.webservices.port + "/api/rainbow/multienvironments/v1.0/environments?loginEmail=" + encodeURIComponent(login);
            const response = await fetch(url, { method: 'GET' });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();
            return responseData.data;
        }
        catch (error) {
            let errorMessage = 'getUserEnvironments for login "' + login + '" failure : ';

            // Handle complete failure (no result info)
            if (error.status === 0 || error.status === -1) { errorMessage += "No server response"; }
            else if (error.status === 500 && error.json) {
                const responseData: any = await error.json();
                errorMessage += responseData.errorDetails;
            }

            // Log pb information
            this.logger.info(`[AuthService] ${errorMessage}`);
            this.logger.info('[AuthService] getUserEnvironment -- continue with default environment');
            return null;
        }
    }

    public async getSAMLConfiguration(companyId: string, ssoId: string): Promise<string> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/saml/${companyId}/ssoId/${ssoId}/metadata.xml`;
            const response: Response = await fetch(url, { method: 'GET', headers: this.getRequestHeader() });
            if (!response.ok) { throw response; }
            this.logger.debug("[AuthService] getSAMLConfiguration -- SUCCESS");
            return await response.text();
        }
        catch (error) {
            this.logger.error(`[AuthService] getSAMLConfiguration -- FAILURE -- ${JSON.stringify(error)}`);
            throw new Error('getSAMLConfiguration failure');
        }
    }

    public async getFingerPrint(): Promise<string> {
        if (!this.browserFingerprint) {
            const browserTests = [
                "audio", "availableScreenResolution", "canvas", "colorDepth", "cookies", "cpuClass", "deviceDpi",
                "doNotTrack", "indexedDb", "installedFonts", "language", "localStorage", "pixelRatio", "platform",
                "plugins", "processorCores", "screenResolution", "sessionStorage", "timezoneOffset", "touchSupport",
                "userAgent", "webGl"
            ];
            if (imprint) this.browserFingerprint = await imprint.test(browserTests);
            else this.browserFingerprint = 'NoImprintImplementation';
        }
        return this.browserFingerprint;
    }

    private formatDuration(timestamp: number): string {
        const duration = intervalToDuration({ start: 0, end: timestamp });
        return formatDuration(duration, { format: ['months', 'days', 'hours', 'minutes', 'seconds'] });
    }

    private createError(message: string, details: string = null, detailsCode: number = null, detailsData: any = null, localError: boolean = false): Error {
        const error: any = new Error(message);
        error.details = details;
        error.detailsCode = detailsCode;
        error.detailsData = detailsData;
        error.localError = localError;
        return error;
    }

    private extractUserInfo(responseData: any) {
        this.loggedInUser = responseData;
        this.login = this.loggedInUser.loginEmail;
        this.userId = this.loggedInUser.id;
        this.jidIm = this.loggedInUser.jid_im;
        this.jidPwd = this.loggedInUser.jid_password;
        this.jidTel = this.loggedInUser.jid_tel;
        this.initialized = this.loggedInUser.isInitialized;
        this.isGuest = this.loggedInUser.guestMode;
        this.companyId = this.loggedInUser.companyId;
        this.xmppDomain = this.jidIm.split("@")[1];
        if (this.loggedInUser.language) {
            this.settingsService.setAppliLangageCodeFromServer(this.loggedInUser.language);
        }
    }

    // ***************************************************************************************
    // ** AUTOMATIC AUTHENTICATION MECHANISM                                                 *
    // ***************************************************************************************

    public async autoAuth(noAutoLoginPatterns?: string[], commandLineInfo?: any): Promise<AutoAuthResult> {
        // Extract location search parameters
        const searchParams: any = this.getLocationSearchParameters();

        // Handle noAutoAuth parameter
        if (searchParams.hasOwnProperty('noAutoLogin')) {
            this.logger.info(`[AuthService] autoAuth -- IGNORED -- noAuthoLogin parameters`);
            return { type: AUTO_AUTH_TYPES.NoAuth };
        }

        // Handle noAutoAuth patterns
        if (noAutoLoginPatterns?.find((pattern: string) => {
            const patternMatch = window.location.hash.startsWith(`#/${pattern}`);
            if (patternMatch) this.logger.info(`[AuthService] autoAuth -- IGNORED -- pattern ${pattern} detected`);
            return patternMatch;
        })) { return { type: AUTO_AUTH_TYPES.NoAuth }; }

        // Handle Desktop keyChain auth information
        const keyChainAuthInfo = await this.getKeyChainDesktopAuthInfo();
        if (keyChainAuthInfo && keyChainAuthInfo.login && keyChainAuthInfo.passwd) {
            try {
                await this.logon(keyChainAuthInfo.login, keyChainAuthInfo.passwd, true);
                this.logger.info(`[AuthService] autoAuth -- authentication by keyChain ${keyChainAuthInfo.login} -- SUCCESS`);
                return { type: AUTO_AUTH_TYPES.KeyChain, status: AUTH_STATUS.Success };
            }
            catch (error) {
                this.logger.error(`[AuthService] autoAuth -- authentication by keyChain -- FAILURE -- ${error.message} `);
                return { type: AUTO_AUTH_TYPES.KeyChain, status: AUTH_STATUS.Failure, error, authInfo: keyChainAuthInfo };
            }
        }

        // Handle Login/Password commandLine authentication (rainbow:// or https://)
        if (!commandLineInfo) {
            this.logger.info(`[AuthService] autoAuth -- get commandLine`);
            const commandLine = await this.getCommandLine();
            commandLineInfo = this.handleAuthCommandLine(commandLine);
        }
        if (commandLineInfo) {
            try {
                // Handle domain change
                let currentDomain = config.xmpp.currentServer;
                if (currentDomain.startsWith('web-') || currentDomain.startsWith('web.')) {
                    this.logger.error(`[AuthService] autoAuth -- current domain ${currentDomain} is prefixed, remove prefix`);
                    currentDomain = currentDomain.slice(4);
                    this.logger.error(`[AuthService] autoAuth -- current domain is now ${currentDomain}`);
                }
                /* MCO : COMMENT FOR SSO TEAMS */
                if (currentDomain !== commandLineInfo.domain && !commandLineInfo.domain.includes('localhost')) {
                    this.logger.error(`[AuthService] autoAuth -- authentication by command line -- FAILURE -- Wrong domain detected (expected: ${commandLineInfo.domain}, current: ${currentDomain})`);
                    commandLineInfo.error = 'WrongDomain';
                    return { type: AUTO_AUTH_TYPES.CommandLine, status: AUTH_STATUS.Failure, authInfo: commandLineInfo };
                }
                // Handle temporary Microsoft token
                if (commandLineInfo.token) {
                    const token = await this.getRainbowTokenFromTemporaryToken(commandLineInfo.token);
                    await this.logonWithToken(token);
                    this.logger.info(`[AuthService] autoAuth -- authentication by token from commandLine -- SUCCESS`);
                    return { type: AUTO_AUTH_TYPES.AuthToken, status: AUTH_STATUS.Success };
                }
                else if (!commandLineInfo.passwd) return { type: AUTO_AUTH_TYPES.CommandLine, status: AUTH_STATUS.Failure, authInfo: commandLineInfo };
                await this.logon(commandLineInfo.login, commandLineInfo.passwd, true);

                this.logger.info(`[AuthService] autoAuth -- authentication by command line -- SUCCESS`);
                return { type: AUTO_AUTH_TYPES.CommandLine, status: AUTH_STATUS.Success };
            }
            catch (error) {
                this.logger.error(`[AuthService] autoAuth -- authentication by command line -- FAILURE -- ${error.message} `);
                return { type: AUTO_AUTH_TYPES.CommandLine, status: AUTH_STATUS.Failure, error, authInfo: commandLineInfo };
            }
        }

        // Get SSO token authentication (provided in the url by an Identity Provider)
        const authToken = searchParams.tkn;
        if (authToken) {
            this.logger.info("[AuthService] autoAuth -- found SSO token");

            //hide token from URL once we got it
            if (window.location?.href.indexOf("tkn=") !== -1) {
                window.location.href = window.location.href.replace(/tkn=[^&]*/g, "");
            }
        }

        // No SSO token but SSO error found
        const errorCode = searchParams.errcode;
        if (errorCode) {
            const errType = searchParams.errtype;
            const errmsg = searchParams.errmsg;
            this.logger.error(`[AuthService] autoAuth -- SSO error -- ${errorCode} -- ${errType} -- ${errmsg}`);
            return { type: AUTO_AUTH_TYPES.AuthToken, status: AUTH_STATUS.Failure, errorCode, authInfo: keyChainAuthInfo };
        }

        // Handle authentication with authToken provided by SSO or use localStorage authToken
        const errorLabelType = authToken ? 'SSO' : 'Legacy';
        try {
            await this.logonWithToken(authToken);
            this.logger.info(`[AuthService] autoAuth -- authentication by token (${errorLabelType}) -- SUCCESS`);
            return { type: AUTO_AUTH_TYPES.AuthToken, status: AUTH_STATUS.Success };
        }
        catch (error) {
            // Handle specific SSO error
            if (error instanceof InvalidTokenError) {
                this.logger.error(`[AuthService] autoAuth -- authentication by token (${errorLabelType}) -- FAILURE`);
                return { type: AUTO_AUTH_TYPES.AuthToken, status: AUTH_STATUS.Failure, errorCode: 'errorInvalidToken' };
            }
            return { type: AUTO_AUTH_TYPES.AuthToken, status: AUTH_STATUS.Failure, authInfo: keyChainAuthInfo };
        }
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    public async getMainProcessArguments(): Promise<string[]> {
        // This method is overriden by centralized service on desktop platform
        return [];
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    public async getKeyChainDesktopAuthInfo(): Promise<any> {
        // This method is overriden by centralized service on desktop platform
        return null;
    }

    public async getCommandLine(): Promise<string | null> {
        try {
            // Desktop application case
            const parameters = await this.getMainProcessArguments();
            if (parameters && parameters.length > 1) {
                const rainbowParameters = parameters[parameters.length - 1];
                if (rainbowParameters.startsWith('rainbow:')) {
                    let commandLine = rainbowParameters.substring("rainbow://".length);
                    commandLine = commandLine !== '/' ? commandLine : null;
                    if (localStorage.showCommandLine) this.logger.info(`[AuthService] getCommandLine -- found 'rainbow://' command line : ${commandLine}`);
                    else this.logger.info(`[AuthService] getCommandLine -- found 'rainbow://' command line`);
                    return commandLine;
                }
            }

            // Desktop and Web application case
            const commandLine = window.location.href ? window.location.href.slice(window.location.protocol === 'http:' ? 7 : 8) : '';
            if (commandLine.length) {
                this.logger.info(`[AuthService] getCommandLine -- found 'https://' command line`);
                return commandLine;
            }
        }
        catch (error) {
            this.logger.error(`[AuthService] getCommandLine -- failure-- ${error.message} `);
        }
        return null;
    }

    public handleAuthCommandLine(commandLine: string): any {
        if (!commandLine) return undefined;

        const loginIndex = commandLine.indexOf('?login=');
        if (loginIndex === -1) return undefined;
        const params = commandLine.slice(loginIndex + 7).split('&');

        // Handle login
        let login = params[0] ? params[0] : null;
        if (login && login.length > 1) {
            login = decodeURIComponent(login);
            if (login.indexOf('@') === -1) login = atob(login);
        }

        // Handle password
        let passwd = params[1]?.includes('pwd=') ? params[1].slice("pwd=".length) : undefined;
        if (passwd) passwd = decodeURIComponent(passwd);

        // Handle token
        const token = params[1]?.includes('tkn=') ? params[1].slice('tkn='.length) : undefined;

        // Handle domain
        const charBeforeQuestionMark = commandLine.charAt(loginIndex - 1);
        const domainIndex = charBeforeQuestionMark === '/' ? loginIndex - 1 : loginIndex;
        const domain = commandLine.slice(0, domainIndex);

        const protocol = domain.includes('localhost') ? 'http://' : 'https://'; // Debug stuff
        const server = domain.includes('localhost') ? 'demo.openrainbow.org' : domain; // Debug stuff
        this.logger.info(`[AuthService] handleAuthCommandLine -- domain: ${domain}, login: ${login}, protocol: ${protocol}, server: ${server}`);
        if (token) this.logger.info(`[AuthService] handleAuthCommandLine -- token: ${token}`);
        return { domain, login, passwd, token, protocol, server };
    }

    public handleAuthBackChannelCommandLine(commandLine: string): void {
        if (!commandLine) return;
        this.logger.info(`[AuthService] handleAuthBackChannelCommandLine`);

        // notify the login page with the successful backchannel authent command
        this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_BACKCHANNEL_REDIRECT_COMMAND);
    }

    public getLocationSearchParameters(): any {
        const search = window.location.search ? window.location.search : window.location.hash.slice(window.location.hash.indexOf('?'));
        return search.slice(1).split('&').reduce((obj: any, keyValue: string) => {
            const data = keyValue.split('='); obj[data[0]] = data[1]; return obj;
        }, {});
    }

    /**
     * Retrieve multifactor authentication information from server
     */
    public async retrieveMfaInformation(): Promise<MultiFactorAuthInformation> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.userId}/mfa`;
            const response = await fetch(url, { method: 'GET', headers: this.getRequestHeader() });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();
            const mfaInfo = MultiFactorAuthInformation.createMFAInfoFromData(responseData.data);
            return mfaInfo;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveMfaInformation');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * This API allows Rainbow users to verify that rainbow multifactor authentication is operational
     * @param token - 6-digits TOTP code
     */
    public async verifyMFAInformation(token: string): Promise<MultiFactorAuthVerifInfo> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.userId}/mfa/verify`;
            const response = await fetch(url, { method: 'POST', headers: this.getPostHeader(), body: JSON.stringify({ token: token }) });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();
            const mfaVerifInfo: MultiFactorAuthVerifInfo = { mfaType: responseData.data?.mfaType, mfaRecoveryCode: responseData.data?.mfaRecoveryCode };
            return mfaVerifInfo;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'verifyMFAInformation');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * Allows Rainbow users to enable Multifactor Authentication in order to finalize activation process
     */
    public async enableMFA(): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.userId}/mfa`;
            const response = await fetch(url, { method: 'PUT', headers: this.getPostHeader() });
            if (!response.ok) { throw response; }
            return;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'enableMFA');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * This API allows Rainbow users to login during a short duration for executing some specific operation concerning a feature given in body content
     * If login is successful, the API returns a JSON Web Token (<abbr title="JSON Web Token">JWT) which has to be provided by clients for all Rainbow APIs
     * requiring this user authentication with short duration:
     * The JWT is valid only for a given time period.
     * The JWT cannot be renewed.
     * Once the JWT expired, user must login again using this API.
     * @param login - loginEmail of user trying to authenticate
     * @param password - password of user trying to authenticate
     * @param feature - feature for which the login is for. Default: 'mfa'
     * @param duration - duration in seconds of the limited login. Default: 600 seconds
     */
    public async loginCheck(login: string, password: string, feature: string = 'mfa', duration: number = 600): Promise<MultiFactorAuthFeatureInfo> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/limitedlogin?duration=${duration}`;

            // Compute authentication request headers
            const headers: any = {};
            headers.Authorization = `Basic ${window.btoa(unescape(encodeURIComponent(login + ":" + password)))}`;
            headers.Accept = 'application/json';
            this.setInformationHeaders(headers);
            headers['Content-Type'] = "application/json";

            if (config.rainbowApiClientKey) {
                const clientKey = config.rainbowApiClientKey + ":" + SHA256(config.appModuleKey + password);
                headers["x-rainbow-app-auth"] = "Basic " + window.btoa(unescape(encodeURIComponent(clientKey)));
            }

            const response = await fetch(url, { method: 'POST', headers, credentials: 'include', body: JSON.stringify({ feature: feature }) });
            if (!response.ok) { throw response; }

            const responseData = await response.json();
            if (responseData) {
                return new MultiFactorAuthFeatureInfo(responseData.feature, responseData.token, responseData.mfa);
            }

            throw new Error("[AuthService] loginCheck - Error in data returned");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'loginCheck');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * Allows Rainbow users to disable multifactor authentication
     * @param token - to be used for disable API call
     */
    public async disableMFA(token: string): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.userId}/mfa`;
            // Compute authentication request headers
            const headers: any = {};
            headers.Authorization = `Bearer ${token}`;
            headers.Accept = 'application/json';
            const response = await fetch(url, { method: 'DELETE', headers, credentials: 'include' });
            if (!response.ok) { throw response; }
            return;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'disableMFA');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * Realize the second authication with a totp code as second factor using
     * @param mfaToken - A valid JWT token is generated by the API and returned as answer of a call to the route GET /api/rainbow/authentication/v1.0/login giving a valid user & password
     * @param mfaTotpCode -TOTP code
     * @param trustedApp - indicates if MFA policy is configured to authorize trusted app to not ask for totp code at next login
     */
    public async loginUsingToTp(mfaToken: string, mfaTotpCode: number, trustedApp: boolean = false): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/mfa/totp/login`;

            // Compute authentication request headers
            const headers: any = {};
            headers.Authorization = `Bearer ${mfaToken}`;
            headers.Accept = 'application/json';
            this.setInformationHeaders(headers);
            headers['Content-Type'] = "application/json";
            // Set id associated to the application which realizes the login operation
            // This id will be checked to the ones stored in datababase in case of multifactor authentication is activated for the user and configured to be run only for only unstrusted application
            const trustedAppId = randomString();
            localStorage.setItem("x-rainbow-trusted-appid", trustedAppId);
            headers["x-rainbow-trusted-appid"] = trustedAppId;
            headers["x-rainbow-trusted-appname"] = `webApp-${trustedAppId}`;

            const body = JSON.stringify({ mfaTotpCode: mfaTotpCode, trustedApp: trustedApp });
            const response = await fetch(url, { method: 'POST', headers, credentials: 'include', body });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();

            // Extract authToken from response
            this.token = responseData.token;
            this.extractUserInfo(responseData.loggedInUser);

            // Update credentials local storage storing policy
            if (trustedApp) {
                this.logger.info("[AuthService] Store authentication info in local storage");
                localStorage.setItem('login', this.login);
                localStorage.setItem('id', this.userId);
                localStorage.setItem('token', this.token);
                localStorage.setItem('authType', this.authType);
            }
            else { this.removeCredentials(); }

            localStorage.setItem('firstUse', 'false');
            this.startTokenSurvey();
            return;

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'loginUsingToTp');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }

    }

    /**
     * Realize the second authication with a recovery code as second factor using
     * @param mfaToken - A valid JWT token is generated by the API and returned as answer of a call to the route GET /api/rainbow/authentication/v1.0/login giving a valid user & password
     * @param mfaRecoveryCode - recovery code
     */
    public async loginUsingRecoveryCode(mfaToken: string, mfaRecoveryCode: string): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/mfa/recovery/login`;

            // Compute authentication request headers
            const headers: any = {};
            headers.Authorization = `Bearer ${mfaToken}`;
            headers.Accept = 'application/json';
            this.setInformationHeaders(headers);
            headers['Content-Type'] = "application/json";
            const body = JSON.stringify({ mfaRecoveryCode: mfaRecoveryCode });
            const response = await fetch(url, { method: 'POST', headers, credentials: 'include', body });
            if (!response.ok) { throw response; }
            const responseData: any = await response.json();

            // Extract authToken from response
            this.token = responseData.token;

            this.extractUserInfo(responseData.loggedInUser);
            this.removeCredentials();
            localStorage.setItem('firstUse', 'false');
            this.startTokenSurvey();

            return;

        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'loginUsingRecoveryCode');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * Allows Rainbow users to reset recovery code for multifactor authentication
     * @param token - to be used for reset API call
     */
    public async resetRecoveryCode(token: string): Promise<string> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.userId}/mfa/recovery`;
            // Compute authentication request headers
            const headers: any = {};
            headers.Authorization = `Bearer ${token}`;
            headers.Accept = 'application/json';
            const response = await fetch(url, { method: 'DELETE', headers, credentials: 'include' });
            if (!response.ok) { throw response; }
            const responseData = await response.json();

            return responseData?.mfaRecoveryCode;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'resetRecoveryCode');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * Allows Rainbow users to delete all trusted applications using
     * @param token - to be used for disable API call
     */
    public async disableAllMfaTrustedApps(token: string): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.userId}/mfa/trusted`;
            // Compute authentication request headers
            const headers: any = {};
            headers.Authorization = `Bearer ${token}`;
            headers.Accept = 'application/json';
            const response = await fetch(url, { method: 'DELETE', headers, credentials: 'include' });
            if (!response.ok) { throw response; }
            return;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'disableAllMfaTrustedApps');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * Allows Rainbow users to delete a trusted application
     * @param token - to be used for disable API call
     * @param appId - application unique id
     */
    public async disableMfaTrustedApp(token: string, appId: string): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.userId}/mfa/trusted/${appId}`;
            // Compute authentication request headers
            const headers: any = {};
            headers.Authorization = `Bearer ${token}`;
            headers.Accept = 'application/json';
            const response = await fetch(url, { method: 'DELETE', headers, credentials: 'include' });
            if (!response.ok) { throw response; }
            return;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'disableMfaTrustedApp');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * Get a "standard" Rainbow JWT from a temporary microsoft token.
     * This API can only be used by the "Rainbow official Desktop application".
     * @param temporaryToken - a temporary microsoft token
     */
    public async getRainbowTokenFromTemporaryToken(temporaryToken: string): Promise<string> {
        try {
            const challenge = randomString(20);
            const url = `${config.restServerUrl}/api/rainbow/authentication/v1.0/microsoft/sso/jwt?challenge=${challenge}`;

            this.logger.info(`[AuthService] getRainbowTokenFromTemporaryToken()`);
            // Compute authentication request headers
            const headers: any = {};
            const clientKey = config.rainbowApiClientKey + ":" + SHA256(config.appModuleKey + challenge);

            headers["x-rainbow-app-auth"] = "Basic " + window.btoa(unescape(encodeURIComponent(clientKey)));
            headers['Authorization'] = `Bearer ${temporaryToken}`;
            headers.Accept = 'application/json';
            this.setInformationHeaders(headers);
            const response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw response; }
            const jsonResponse = await response.json();
            return jsonResponse.token;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getRainbowTokenFromTemporaryToken');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * Ask the backend to send a verification code by email or sms to the identified user.
     * @param uid - User identifier (loginEmail or loginPhone)
     * @param country - country code (alpha3) of the loginPhone 'used for passwordless by phone number authentication method
     */
    public async sendPasswordlessCodeToUser(uid: string, country?: string): Promise<{
        codeLength: number;
        codeDuration: number;
        expirationDate: string;
        smsSent: boolean;
        emailSent: boolean;
        smsSentTo?: string;
        emailSentTo?: string; }> {
        try {
            const url = new URL(`${config.restServerUrl}/api/rainbow/authentication/v1.0/passwordless/code`);
            url.searchParams.set("uid", uid);
            if (country) {
                url.searchParams.set("country", country);
            }
            const headers: any = {};
            headers.Accept = 'application/json';
            const response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) { throw response; }

            const jsonResponse = await response.json();
            return jsonResponse.data;

            //return { codeLength: 6, codeDuration: 3600, expirationDate: "", smsSent: false, emailSent: true, emailSentTo: uid.replace('@', '*') };
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sendPasswordlessCodeToUser');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    /**
     * This API allows Rainbow users to login without password, but with a verification code sent by mail or sms
     * @param uid - User identifier (loginEmail or loginPhone)
     * @param code - OTP code
     * @param challenge - for autorized application identification
     * @param rememberMe - Indicates wether the browser must memorize the token and do auto token renewals
     */
    public async loginUsingPasswordlessLogin(uid: string, code: string, challenge: string, rememberMe: boolean): Promise<void> {
        try {
            const url = new URL(`${config.restServerUrl}/api/rainbow/authentication/v1.0/passwordless/login`);
            url.searchParams.set("challenge", challenge);

            const headers: any = {};
            headers['Content-Type'] = "application/json";
            headers.Accept = 'application/json';
            headers["x-rainbow-app-auth"] = "Basic " + this.getRainbowAppAuth(config.rainbowApiClientKey, config.appModuleKey, challenge);
            const jsonBody = {
                uid,
                code
            };
            const response = await fetch(url.toString(), { method: 'POST', headers, body: JSON.stringify(jsonBody) });
            if (!response.ok) { throw response; }

            const jsonResponse: any = await response.json();
            const responseData: any = jsonResponse;

            // Extract authToken from response
            this.token = responseData.token;
            this.extractUserInfo(responseData.loggedInUser);

            // Update credentials local storage storing policy
            if (rememberMe) {
                this.logger.info("[AuthService] Store authentication info in local storage");
                localStorage.setItem('login', this.login);
                localStorage.setItem('id', this.userId);
                localStorage.setItem('token', this.token);
                localStorage.setItem('authType', this.authType);
            }
            else { this.removeCredentials(); }

            localStorage.setItem('firstUse', 'false');
            this.startTokenSurvey();
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'sendPasswordlessCodeToUser');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }

    // Backchannel authentication for Sso Azure Cond access
    public async retrieveBackChannelUserToken(authSettings: AuthSettings, rememberMe: boolean, renewSession?: boolean): Promise<string> {
        this.authSettings = authSettings;
        this.rememberMe = rememberMe;
        this.backChannelRenewSession = renewSession;

        return new Promise((resolve, reject) => {
            // listen events for token retrieval operation: success, failure or intermediate redirect
            const backChannelSubscription: Subscription = this.subscribe((event: RBEvent) => {
                if (event.name === AuthServiceEvents.RAINBOW_ON_AUTH_BACKCHANNEL_SUCCESS) {
                    this.logger.info(`[authService] backchannel event received ${event.name}`);
                    resolve(this.token);
                    backChannelSubscription.unsubscribe();
                }
                if (event.name === AuthServiceEvents.RAINBOW_ON_AUTH_BACKCHANNEL_FAILURE) {
                    this.logger.info(`[authService] backchannel event received ${event.name}`);
                    reject();
                    backChannelSubscription.unsubscribe();
                }
                if (event.name === AuthServiceEvents.RAINBOW_ON_AUTH_BACKCHANNEL_REDIRECT_COMMAND) {
                    this.logger.info(`[authService] backchannel event received ${event.name}`);
                    // We have received the redirect command sent by the default browser:
                    // this means that the token is available in backend
                    // Let's do a last polling to retrieve the token and the logged in user data.
                    // This will also end the polling session in backend
                    if (this.backChannelPollingTimeout) {
                        clearTimeout(this.backChannelPollingTimeout);
                    }
                    this.startSsoCondAccessPoll();
                }
            });
            // start token polling
            this.startSsoCondAccessTokenPolling();
        });
    }

    private startSsoCondAccessTokenPolling(): void {
        this.backChannelPollingTimeout = setTimeout(this.startSsoCondAccessPoll.bind(this), 1000);
    }

    public stopPollingBackChannelUserToken() {
        if (this.backChannelPollingTimeout) {
            clearTimeout(this.backChannelPollingTimeout);
            this.backChannelRenewSession = undefined;
        }
    }

    private async startSsoCondAccessPoll(): Promise<void> {
        // retrieve backchannel jwt token, and retry while operation fails
        try {
            await this.pollingBackChannelUserToken(this.authSettings.backChannelSession.id, this.authSettings.backChannelSession.token);
            this.logger.info(`[authService] startSsoCondAccessPoll success`);

            this.backChannelPollingTimeout = null;
            this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_BACKCHANNEL_SUCCESS);
        } catch (error) {
            if (error.errorDetailsCode === 401208) {
                // Sso Azure Cond Access not yet completed : start again polling
                this.logger.info(`[authService] startSsoCondAccessPoll error ${error.message} : not yet completed => start again polling`);
                this.startSsoCondAccessTokenPolling();
                return;
            }
            this.logger.info(`[authService] startSsoCondAccessPoll unexpected error ${error.message} => stop polling url for token`);
            this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_BACKCHANNEL_FAILURE);
        }
    }

    /**
     * This API allows Rainbow users to login without password, but with a verification code sent by mail or sms
     * @param uid - User identifier (loginEmail or loginPhone)
     * @param code - OTP code
     * @param challenge - for autorized application identification
     * @param rememberMe - Indicates wether the browser must memorize the token and do auto token renewals
     */
    private async pollingBackChannelUserToken(backChannelSessionId: string, pollingToken: string): Promise<void> {
        try {
            const url = new URL(`${config.restServerUrl}/api/rainbow/authentication/v1.0/backchannel/${backChannelSessionId}/jwt`);

            const headers: any = {};
            headers.Authorization = `Bearer ${pollingToken}`;
            headers.Accept = 'application/json';

            const response = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) { throw response; }

            const jsonResponse: any = await response.json();
            const responseData: any = jsonResponse;

            // Extract authToken from response
            this.token = responseData.token;

            if (this.backChannelRenewSession) {
                localStorage.setItem('token', this.token);
                this.sessionShouldBeUpdated = false;
                this.sendEvent(AuthServiceEvents.RAINBOW_ON_AUTH_SESSION_SHOULD_RENEW, false);
                this.backChannelRenewSession = false;
            } else {
                this.extractUserInfo(responseData.loggedInUser);

                // Update credentials local storage storing policy
                if (this.rememberMe) {
                    this.logger.info("[AuthService] Store authentication info in local storage");
                    localStorage.setItem('login', this.login);
                    localStorage.setItem('id', this.userId);
                    localStorage.setItem('token', this.token);
                    localStorage.setItem('authType', this.authType);
                }
                else { this.removeCredentials(); }

                localStorage.setItem('firstUse', 'false');
                this.startTokenSurvey();
            }
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'pollingBackChannelUserToken');
            this.logger.error(errorInfo?.message);
            throw (errorInfo.error);
        }
    }
}
